import { Fragment, useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useViewScopeStore } from "../store/viewScopeStore";
import { useTasksStore } from "../store/tasksStore";
import useTenantWebSocket from "../hooks/useTenantWebSocket";
import { TEST_IDS } from "../constants/testIds";
import { LIST_PALETTE, DEFAULT_LIST_PALETTE, getListPalette } from "../constants/listPalette";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import TaskDialog from "../components/tasks/TaskDialog";
import TaskRightPanel from "../components/tasks/TaskRightPanel";
import TaskListView from "../components/tasks/TaskListView";
import { ArrowLeft, Plus, Megaphone, Zap, Trash2, X, Pencil, Palette, Smile, Search, FolderOpen, UploadCloud, Loader2 } from "lucide-react";
import { isAssignedToUser } from "../lib/taskPermissions";
import KanbanView from "../components/tasks/KanbanView";
import CalendarView from "../components/tasks/CalendarView";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "../components/ui/context-menu";

// The emoji picker pulls in a sizeable bundle (~150kb of emoji metadata),
// so we lazy-load it and only mount it when the user actually opens the
// "Emoji seç" dialog.
const EmojiPicker = lazy(() => import("emoji-picker-react"));

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const listFilter = searchParams.get("list") || null;
  const overdueOnly = searchParams.get("overdue") === "1";
  const { t } = useTranslation();
  const { tenant, user } = useAuthStore();
  const { scope } = useViewScopeStore();
  const isMe = scope === "me";
  const {
    tasks,
    lists,
    users,
    sortBy,
    groupBy,
    showCompleted,
    showRecurring,
    setSortBy,
    setGroupBy,
    setShowCompleted,
    setShowRecurring,
    fetchLists,
    fetchTasks,
    fetchUsers,
    createTask,
    updateTask,
    deleteTask,
    createList,
    updateList,
    deleteList,
  } = useTasksStore();

  const [project, setProject] = useState(null);
  const [newListName, setNewListName] = useState("");
  const [showNewList, setShowNewList] = useState(false);
  const [panelTask, setPanelTask] = useState(null);
  const [panelInitialTab, setPanelInitialTab] = useState("details");
  const [dialogTask, setDialogTask] = useState(null);
  const [view, setView] = useState("list");
  // Monotonically increasing token used to imperatively open TaskListView's
  // composer from the "Görev/not ekle" button that lives next to the tabs.
  const [addComposerToken, setAddComposerToken] = useState(0);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [announceBody, setAnnounceBody] = useState("");
  const [announceExpiresAt, setAnnounceExpiresAt] = useState("");
  const [isMeeting, setIsMeeting] = useState(false);
  const [meetingMode, setMeetingMode] = useState("online"); // "online" | "physical"
  const [meetingLink, setMeetingLink] = useState("");
  const [meetingPlace, setMeetingPlace] = useState("");
  const [meetingAt, setMeetingAt] = useState("");
  const [meetingDuration, setMeetingDuration] = useState(60); // minutes
  const [meetingRequired, setMeetingRequired] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  // "Grubu (proje grubu / list) sil" — separate state so it doesn't collide
  // with the project-level delete flow.
  const [deleteListTarget, setDeleteListTarget] = useState(null);
  const [deletingList, setDeletingList] = useState(false);
  // "Grubu düzenle" akışı: yeniden adlandır + emoji seç dialog'ları
  // için ayrı state'ler. `renameTarget` / `emojiTarget` çekildiğinde
  // ilgili dialog açılır.
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [emojiTarget, setEmojiTarget] = useState(null);
  const navigate = useNavigate();

  /**
   * Persist a single field change (name / color / emoji) for a list. Empty
   * strings are allowed — backend stores them as-is so users can clear an
   * emoji or reset the color to "neutral".
   */
  const patchList = async (list, patch, successMsg) => {
    try {
      await updateList(list.id, patch);
      if (successMsg) toast.success(successMsg);
      window.dispatchEvent(new Event("serpix:lists-changed"));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Grup güncellenemedi",
      );
    }
  };

  const openRename = (list) => {
    setRenameTarget(list);
    setRenameValue(list?.name || "");
  };

  const submitRename = async () => {
    if (!renameTarget?.id) return;
    const nextName = renameValue.trim();
    if (!nextName) {
      toast.error("Grup adı boş olamaz");
      return;
    }
    if (nextName === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    setSavingRename(true);
    try {
      await updateList(renameTarget.id, { name: nextName });
      toast.success(`Grup "${nextName}" olarak yeniden adlandırıldı`);
      window.dispatchEvent(new Event("serpix:lists-changed"));
      setRenameTarget(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Grup güncellenemedi",
      );
    } finally {
      setSavingRename(false);
    }
  };

  const deleteListHandler = async () => {
    if (!deleteListTarget?.id) return;
    setDeletingList(true);
    try {
      await deleteList(deleteListTarget.id);
      toast.success(`"${deleteListTarget.name}" grubu silindi`);
      // If the deleted group was the active filter, clear it.
      if (listFilter === deleteListTarget.id) {
        const next = new URLSearchParams(searchParams);
        next.delete("list");
        setSearchParams(next, { replace: true });
      }
      // Nudge the sidebar to refetch this project's lists.
      window.dispatchEvent(new Event("serpix:lists-changed"));
      setDeleteListTarget(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Grup silinemedi",
      );
    } finally {
      setDeletingList(false);
    }
  };

  const deleteProject = async () => {
    if (!project?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success(`"${project.name}" projesi silindi`);
      setDeleteOpen(false);
      // Notify sidebar / other pages that the projects list changed so
      // they can refresh without a full page reload.
      window.dispatchEvent(new Event("serpix:projects-changed"));
      navigate("/app/projects");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Proje silinemedi",
      );
    } finally {
      setDeleting(false);
    }
  };

  const formatMeetingDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("tr-TR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const buildFinalBody = () => {
    const raw = announceBody.trim();
    if (!isMeeting) return raw;
    const lines = ["📅 TOPLANTI DUYURUSU"];
    if (meetingRequired) lines.push("🔴 ZORUNLU KATILIM");
    if (raw) lines.push("", raw);
    lines.push("");
    if (meetingAt) lines.push(`🕒 Tarih & Saat: ${formatMeetingDate(meetingAt)} (${meetingDuration} dk)`);
    if (meetingMode === "online") {
      lines.push("💻 Katılım: Online");
      if (meetingLink.trim()) lines.push(`🔗 Bağlantı: ${meetingLink.trim()}`);
    } else {
      lines.push("📍 Katılım: Fiziksel");
      if (meetingPlace.trim()) lines.push(`📌 Yer: ${meetingPlace.trim()}`);
    }
    return lines.join("\n");
  };

  const publishAnnouncement = async () => {
    const finalBody = buildFinalBody();
    if (!finalBody) return;
    if (isMeeting) {
      if (!meetingAt) {
        toast.error("Lütfen toplantı tarih ve saatini girin");
        return;
      }
      if (meetingMode === "online" && !meetingLink.trim()) {
        toast.error("Lütfen online toplantı bağlantısını girin");
        return;
      }
      if (meetingMode === "physical" && !meetingPlace.trim()) {
        toast.error("Lütfen toplantı yerini girin");
        return;
      }
    }
    setPublishing(true);
    try {
      // Build structured meeting metadata (backend stores it on the message
      // so the AnnouncementBanner can render RSVP buttons, colors, etc.).
      let meetingPayload = null;
      if (isMeeting) {
        const dm = meetingAt ? new Date(meetingAt) : null;
        meetingPayload = {
          is_meeting: true,
          mode: meetingMode,
          link: meetingMode === "online" ? meetingLink.trim() : null,
          place: meetingMode === "physical" ? meetingPlace.trim() : null,
          at: dm && !isNaN(dm.getTime()) ? dm.toISOString() : null,
          duration_minutes: meetingDuration,
          required: !!meetingRequired,
        };
      }
      const { data: msg } = await api.post(`/channels/${projectId}/messages`, {
        body: finalBody,
        meeting: meetingPayload,
      });
      // Convert datetime-local (local TZ) to ISO UTC before sending, if set.
      let expiresIso = null;
      if (announceExpiresAt) {
        const d = new Date(announceExpiresAt);
        if (!isNaN(d.getTime())) expiresIso = d.toISOString();
      }
      // If it's a meeting and no explicit expiry, auto-expire the announcement
      // exactly when the meeting ends (start + duration).
      if (isMeeting && !expiresIso && meetingAt) {
        const dm = new Date(meetingAt);
        if (!isNaN(dm.getTime())) {
          expiresIso = new Date(
            dm.getTime() + meetingDuration * 60 * 1000,
          ).toISOString();
        }
      }
      await api.patch(`/channels/messages/${msg.id}/pin`, {
        expires_at: expiresIso,
      });
      toast.success(
        isMeeting
          ? "Toplantı duyurusu yayınlandı"
          : expiresIso
            ? "Duyuru yayınlandı — belirlenen tarihte otomatik kaldırılacak"
            : "Duyuru yayınlandı ve sabitlendi",
      );
      setAnnounceOpen(false);
      setAnnounceBody("");
      setAnnounceExpiresAt("");
      setIsMeeting(false);
      setMeetingMode("online");
      setMeetingLink("");
      setMeetingPlace("");
      setMeetingAt("");
      setMeetingDuration(60);
      setMeetingRequired(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Duyuru gönderilemedi");
    } finally {
      setPublishing(false);
    }
  };
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Returns current local time rounded UP to the next 30-minute step,
  // formatted as "YYYY-MM-DDTHH:MM" for <input type="datetime-local">.
  const nowLocalDatetime = () => {
    const d = new Date();
    // Round to next 30-min boundary (00 or 30).
    const minutes = d.getMinutes();
    const rounded = minutes <= 30 ? (minutes === 0 ? 0 : 30) : 60;
    if (rounded === 60) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    } else {
      d.setMinutes(rounded);
    }
    d.setSeconds(0);
    d.setMilliseconds(0);
    const pad = (n) => String(n).padStart(2, "0");
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  };

  // Pre-fill date fields with the current moment whenever the announce
  // dialog opens (only if the user hasn't typed anything yet).
  useEffect(() => {
    if (!announceOpen) return;
    const now = nowLocalDatetime();
    setAnnounceExpiresAt((prev) => prev || now);
    setMeetingAt((prev) => prev || now);
  }, [announceOpen]);

  // When the user toggles the meeting checkbox on for the first time,
  // seed the meeting datetime too (in case dialog was opened earlier).
  useEffect(() => {
    if (isMeeting) {
      setMeetingAt((prev) => prev || nowLocalDatetime());
    }
  }, [isMeeting]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/projects");
      setProject(data.find((p) => p.id === projectId));
    })();
  }, [projectId]);

  useEffect(() => {
    fetchLists(projectId);
    fetchTasks(projectId);
    fetchUsers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useTenantWebSocket((msg) => {
    if (!msg?.event) return;
    if (msg.event.startsWith("task.") || msg.event.startsWith("comment.")) {
      fetchTasks(projectId);
    }
  });

  const createListHandler = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    try {
      await createList({ project_id: projectId, name: newListName });
      setNewListName("");
      setShowNewList(false);
      toast.success("List created");
      // Keep sidebar in sync.
      window.dispatchEvent(new Event("serpix:lists-changed"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openTask = (task, tab = "details") => {
    setPanelInitialTab(tab);
    setPanelTask(task);
  };

  // Deep-link support — when Görev Ekleri (or any external link) sends
  // us here with `?task=<id>`, auto-open the side panel for that task.
  // Runs whenever the task list refreshes so a link that hits the page
  // before tasks are loaded still resolves. Consumed once → the query
  // param is stripped so back-nav doesn't re-open the panel every time.
  useEffect(() => {
    const wanted = searchParams.get("task");
    if (!wanted || tasks.length === 0) return;
    const t = tasks.find((x) => x.id === wanted);
    if (t) {
      openTask(t);
      const next = new URLSearchParams(searchParams);
      next.delete("task");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tasks]);

  const expandToDialog = () => {
    setDialogTask(panelTask);
    setPanelTask(null);
  };

  // Wrapper that surfaces 403 (reassign permission etc.) as a user-facing toast
  const safeUpdateTask = async (id, patch) => {
    try {
      return await updateTask(id, patch);
    } catch (err) {
      const status = err?.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (status === 403) {
        toast.error(detail || t("tasks.reassign_locked_hint"));
      } else if (status === 400) {
        toast.error(detail || t("common.error"));
      } else {
        toast.error(detail || t("common.error"));
      }
      throw err;
    }
  };

  // Filter tasks by ?list=xxx (project group) if present.
  // Also filter to `assignee_id === me` when the global scope is "me".
  // Also filter to overdue-only when the ?overdue=1 chip is active.
  const [taskQuery, setTaskQuery] = useState("");
  const filteredTasks = useMemo(() => {
    let list = tasks;
    if (listFilter) list = list.filter((t) => t.list_id === listFilter);
    if (isMe && user?.id)
      list = list.filter((t) => isAssignedToUser(t, user.id));
    if (overdueOnly) {
      const now = Date.now();
      list = list.filter(
        (t) =>
          t.status !== "done" &&
          t.due_at &&
          new Date(t.due_at).getTime() < now,
      );
    }
    const q = taskQuery.trim().toLocaleLowerCase("tr");
    if (q) {
      list = list.filter((t) =>
        `${t.title || ""} ${t.description || ""}`
          .toLocaleLowerCase("tr")
          .includes(q),
      );
    }
    return list;
  }, [tasks, listFilter, isMe, user?.id, overdueOnly, taskQuery]);

  // Chip badge count: mirrors the global scope.
  const openForMeByList = useMemo(() => {
    const map = {};
    tasks.forEach((tk) => {
      if (tk.status === "done") return;
      if (isMe && user?.id && !isAssignedToUser(tk, user.id)) return;
      map[tk.list_id] = (map[tk.list_id] || 0) + 1;
    });
    return map;
  }, [tasks, isMe, user?.id]);

  // Chip overdue flag: mirrors scope as well.
  const overdueByList = useMemo(() => {
    const now = Date.now();
    const map = {};
    tasks.forEach((tk) => {
      if (tk.status === "done") return;
      if (!tk.due_at) return;
      if (isMe && user?.id && !isAssignedToUser(tk, user.id)) return;
      const due = new Date(tk.due_at).getTime();
      if (isNaN(due) || due >= now) return;
      map[tk.list_id] = true;
    });
    return map;
  }, [tasks, isMe, user?.id]);

  const activeList = useMemo(
    () => (listFilter ? lists.find((l) => l.id === listFilter) : null),
    [listFilter, lists],
  );

  const clearListFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("list");
    setSearchParams(next, { replace: true });
  };

  // ==== Sürükle-bırak hızlı yükleme → proje klasörü ====
  const hasFilesModule = (tenant?.enabled_features || []).some(
    (f) => f === "cop" || f === "expo",
  );
  const filesBasePath = (tenant?.enabled_features || []).includes("cop")
    ? "/app/cop/files"
    : "/app/expo/files";
  const [dragDepth, setDragDepth] = useState(0);
  const [dropUploading, setDropUploading] = useState(false);
  const hasDraggedFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  const uploadDropped = async (droppedFiles) => {
    const list = Array.from(droppedFiles || []).filter((f) => f.size >= 0);
    if (!list.length) return;
    setDropUploading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/folder`);
      let ok = 0;
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder_id", data.folder_id);
        await api.post("/shared-files/files", fd);
        ok++;
      }
      toast.success(
        ok === 1 ? "1 dosya proje klasörüne yüklendi" : `${ok} dosya proje klasörüne yüklendi`,
        {
          action: {
            label: "Klasörü aç",
            onClick: () =>
              navigate(`${filesBasePath}?tab=shared&folder=${data.folder_id}`),
          },
        },
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Dosya yüklenemedi",
      );
    } finally {
      setDropUploading(false);
    }
  };

  return (
    <div
      className="relative space-y-6"
      data-testid="project-detail-page"
      onDragEnter={(e) => {
        if (hasFilesModule && hasDraggedFiles(e)) {
          e.preventDefault();
          setDragDepth((d) => d + 1);
        }
      }}
      onDragOver={(e) => {
        if (hasFilesModule && hasDraggedFiles(e)) e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (hasFilesModule && hasDraggedFiles(e))
          setDragDepth((d) => Math.max(0, d - 1));
      }}
      onDrop={(e) => {
        if (!hasFilesModule || !hasDraggedFiles(e)) return;
        e.preventDefault();
        setDragDepth(0);
        uploadDropped(e.dataTransfer.files);
      }}
    >
      {/* Drop overlay — dosya sürüklenince görünür */}
      {hasFilesModule && (dragDepth > 0 || dropUploading) && (
        <div
          className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-violet-950/40 backdrop-blur-[2px]"
          data-testid="project-drop-overlay"
        >
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-white/70 bg-white/95 px-10 py-8 text-center shadow-2xl">
            {dropUploading ? (
              <Loader2 className="h-10 w-10 animate-spin text-violet-600" />
            ) : (
              <UploadCloud className="h-10 w-10 text-violet-600" />
            )}
            <div className="text-lg font-semibold text-zinc-900">
              {dropUploading ? "Yükleniyor…" : "Dosyaları buraya bırak"}
            </div>
            <div className="text-sm text-zinc-500">
              {dropUploading
                ? "Dosyalar proje klasörüne aktarılıyor"
                : `"${project?.name || "Proje"}" klasörüne yüklenecek`}
            </div>
          </div>
        </div>
      )}
      <div>
        <Link
          to="/app/projects"
          className="mb-3 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("projects.title")}
        </Link>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
              Project
            </div>
            <h1 className="mt-1 break-words font-display text-2xl font-semibold tracking-tight text-zinc-950 sm:text-3xl md:text-4xl">
              {project?.name || "…"}
            </h1>
            {project?.description && (
              <p className="mt-1 text-sm text-zinc-500">{project.description}</p>
            )}
          </div>
          <div className="flex flex-none items-center gap-2 sm:gap-4">
            {(tenant?.enabled_features || []).some((f) => f === "cop" || f === "expo") && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/projects/${projectId}/folder`);
                    const base = (tenant?.enabled_features || []).includes("cop")
                      ? "/app/cop/files"
                      : "/app/expo/files";
                    navigate(`${base}?tab=shared&folder=${data.folder_id}`);
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Klasör açılamadı");
                  }
                }}
                data-testid="project-folder-btn"
                className="w-full gap-2 sm:w-auto"
              >
                <FolderOpen className="h-4 w-4" strokeWidth={2} />
                Proje Klasörü
              </Button>
            )}
            {["super_admin", "ultra_admin"].includes(user?.role) && (
              <Button
                type="button"
                onClick={() => setAnnounceOpen(true)}
                data-testid="publish-announcement-btn"
                className="w-full gap-2 bg-amber-500 text-white hover:bg-amber-600 sm:w-auto"
              >
                <Megaphone className="h-4 w-4" strokeWidth={2} />
                Duyuru yayınla
              </Button>
            )}
            {["super_admin", "ultra_admin"].includes(user?.role) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDeleteConfirmName("");
                  setDeleteOpen(true);
                }}
                data-testid="delete-project-btn"
                className="w-full gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 sm:w-auto"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2} />
                Projeyi sil
              </Button>
            )}
          </div>
        </div>
        {/* Project group filter chips — tam genişlik, her grup tıklanabilir */}
            {lists.length > 0 && (
              <div data-testid="project-group-chips" className="mt-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                    Gruplar:
                  </span>
                  <button
                    type="button"
                    onClick={clearListFilter}
                    data-testid="project-group-all"
                    className={[
                      "rounded-lg px-3 py-1 text-xs font-medium transition-colors",
                      !listFilter
                        ? "bg-zinc-900 text-white"
                        : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200",
                    ].join(" ")}
                  >
                    Tümü
                  </button>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                {lists
                  .filter((l) => (isMe ? (openForMeByList[l.id] || 0) > 0 || !!overdueByList[l.id] : true))
                  .map((l, idx) => {
                  const active = listFilter === l.id;
                  const cnt = openForMeByList[l.id] || 0;
                  const overdue = !!overdueByList[l.id];
                  const canDelete = ["super_admin", "ultra_admin"].includes(user?.role);
                  const palette = getListPalette(l.color);
                  const chipClasses = active ? palette.chipActive : palette.chip;
                  return (
                    <Fragment key={l.id}>
                      {idx > 0 && (
                        <span className="select-none text-sm text-zinc-300" aria-hidden>
                          |
                        </span>
                      )}
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        <div className="group/chip relative inline-flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              const next = new URLSearchParams(searchParams);
                              next.set("list", l.id);
                              setSearchParams(next, { replace: true });
                            }}
                            data-testid={`project-group-chip-${l.id}`}
                            title={`${l.name} — sağ tık: yeniden adlandır, renk veya emoji seç`}
                            className={[
                              "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition-colors",
                              chipClasses,
                            ].join(" ")}
                          >
                            {l.emoji ? (
                              <span
                                data-testid={`project-group-emoji-${l.id}`}
                                className="shrink-0 text-[13px] leading-none"
                                aria-hidden
                              >
                                {l.emoji}
                              </span>
                            ) : (
                              <span className="shrink-0 opacity-50" aria-hidden>
                                #
                              </span>
                            )}
                            <span className="whitespace-nowrap">
                              {l.name}
                            </span>
                            {(cnt > 0 || overdue) && (
                              <span
                                data-testid={`project-group-badge-${l.id}`}
                                className={[
                                  "inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center gap-0.5 rounded-full px-1 text-[10px] font-semibold",
                                  overdue
                                    ? "bg-red-500 text-white"
                                    : active
                                      ? "bg-white/95 text-zinc-900"
                                      : "bg-amber-500 text-white",
                                ].join(" ")}
                                title={
                                  overdue
                                    ? "Bu grupta gecikmiş görev var"
                                    : `${cnt} tamamlanmamış görevin`
                                }
                              >
                                {overdue && (
                                  <span
                                    data-testid={`project-group-overdue-${l.id}`}
                                    className="inline-flex"
                                  >
                                    <Zap
                                      className="h-2.5 w-2.5 fill-white text-white"
                                      strokeWidth={2}
                                    />
                                  </span>
                                )}
                                {cnt > 0 ? cnt : null}
                              </span>
                            )}
                            {canDelete && (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteListTarget(l);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDeleteListTarget(l);
                                  }
                                }}
                                title="Grubu sil"
                                data-testid={`project-group-delete-${l.id}`}
                                className={[
                                  "ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full opacity-0 transition-opacity group-hover/chip:opacity-100 focus:opacity-100",
                                  active
                                    ? "text-white/70 hover:bg-white/10 hover:text-white"
                                    : "text-zinc-500 hover:bg-red-50 hover:text-red-600",
                                ].join(" ")}
                              >
                                <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                              </span>
                            )}
                          </button>
                        </div>
                      </ContextMenuTrigger>
                      <ContextMenuContent
                        className="w-52"
                        data-testid={`project-group-context-menu-${l.id}`}
                      >
                        <ContextMenuItem
                          onSelect={() => openRename(l)}
                          data-testid={`project-group-rename-${l.id}`}
                          className="gap-2"
                        >
                          <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
                          Yeniden adlandır
                        </ContextMenuItem>
                        <ContextMenuSub>
                          <ContextMenuSubTrigger className="gap-2">
                            <Palette className="h-3.5 w-3.5" strokeWidth={2} />
                            Renk seç
                          </ContextMenuSubTrigger>
                          <ContextMenuSubContent className="w-44">
                            <ContextMenuItem
                              onSelect={() =>
                                patchList(l, { color: "" }, "Renk kaldırıldı")
                              }
                              data-testid={`project-group-color-none-${l.id}`}
                              className="gap-2"
                            >
                              <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-zinc-300 bg-white" />
                              <span className="flex-1">Renksiz</span>
                              {!l.color ? (
                                <span className="text-[10px] text-zinc-400">
                                  ✓
                                </span>
                              ) : null}
                            </ContextMenuItem>
                            {LIST_PALETTE.map((p) => (
                              <ContextMenuItem
                                key={p.key}
                                onSelect={() =>
                                  patchList(
                                    l,
                                    { color: p.key },
                                    `Renk: ${p.label}`,
                                  )
                                }
                                data-testid={`project-group-color-${p.key}-${l.id}`}
                                className="gap-2"
                              >
                                <span
                                  className="inline-block h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
                                  style={{ backgroundColor: p.hex }}
                                />
                                <span className="flex-1">{p.label}</span>
                                {l.color === p.key ? (
                                  <span className="text-[10px] text-zinc-400">
                                    ✓
                                  </span>
                                ) : null}
                              </ContextMenuItem>
                            ))}
                          </ContextMenuSubContent>
                        </ContextMenuSub>
                        <ContextMenuItem
                          onSelect={() => setEmojiTarget(l)}
                          data-testid={`project-group-emoji-btn-${l.id}`}
                          className="gap-2"
                        >
                          <Smile className="h-3.5 w-3.5" strokeWidth={2} />
                          Emoji seç
                        </ContextMenuItem>
                        {l.emoji ? (
                          <ContextMenuItem
                            onSelect={() =>
                              patchList(l, { emoji: "" }, "Emoji kaldırıldı")
                            }
                            data-testid={`project-group-emoji-clear-${l.id}`}
                            className="gap-2 text-zinc-500"
                          >
                            <X className="h-3.5 w-3.5" strokeWidth={2} />
                            Emojiyi kaldır
                          </ContextMenuItem>
                        ) : null}
                        {canDelete ? (
                          <>
                            <ContextMenuSeparator />
                            <ContextMenuItem
                              onSelect={() => setDeleteListTarget(l)}
                              data-testid={`project-group-delete-menu-${l.id}`}
                              className="gap-2 text-red-600 focus:bg-red-50 focus:text-red-700"
                            >
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                              Grubu sil
                            </ContextMenuItem>
                          </>
                        ) : null}
                      </ContextMenuContent>
                    </ContextMenu>
                    </Fragment>
                  );
                })}
                </div>
              </div>
            )}
            {(activeList || overdueOnly) && (
              <p
                data-testid="active-list-filter-label"
                className="mt-1 text-xs text-amber-700"
              >
                Filtre:{" "}
                {activeList && (
                  <>
                    <b>{activeList.name}</b> grubu
                  </>
                )}
                {activeList && overdueOnly && " · "}
                {overdueOnly && (
                  <span className="text-red-700">
                    <b>gecikmiş</b> görevler
                  </span>
                )}
              </p>
            )}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {view === "list" && (
              <Button
                size="sm"
                data-testid={TEST_IDS.listAddTaskBtn}
                onClick={() => {
                  setView("list");
                  setAddComposerToken((v) => v + 1);
                }}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              >
                <Plus className="h-3.5 w-3.5" />
                {t("tasks.add_task_or_note")}
              </Button>
            )}
            <TabsList className="justify-start overflow-x-auto bg-zinc-100">
              <TabsTrigger value="list" data-testid="view-list-tab">List</TabsTrigger>
              <TabsTrigger value="kanban" data-testid="view-kanban-tab">Kanban</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="view-calendar-tab">Calendar</TabsTrigger>
            </TabsList>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <Input
                value={taskQuery}
                onChange={(e) => setTaskQuery(e.target.value)}
                placeholder="Görev ara..."
                data-testid="project-task-search-input"
                className="h-9 w-40 pl-8 pr-7 text-sm sm:w-56"
              />
              {taskQuery && (
                <button
                  type="button"
                  onClick={() => setTaskQuery("")}
                  data-testid="project-task-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {taskQuery.trim() && (
              <span
                data-testid="project-task-search-count"
                className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
              >
                {filteredTasks.length} sonuç
              </span>
            )}
          </div>
          {view === "list" &&
            (["super_admin", "ultra_admin"].includes(user?.role) || !!user?.can_create_projects) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowNewList((v) => !v)}
              data-testid={TEST_IDS.newListBtn}
              className="w-full gap-1 border-zinc-300 sm:w-auto"
            >
              <Plus className="h-3 w-3" />
              {t("lists.new_list")}
            </Button>
          )}
        </div>

        <TabsContent value="list" className="mt-6 space-y-4">
          {showNewList && (
            <form
              onSubmit={createListHandler}
              className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-white p-3"
            >
              <Input
                placeholder={t("lists.list_name")}
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                data-testid={TEST_IDS.listNameInput}
                className="max-w-xs"
                autoFocus
              />
              <Button
                type="submit"
                size="sm"
                data-testid={TEST_IDS.listSaveBtn}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                {t("common.add")}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowNewList(false);
                  setNewListName("");
                }}
              >
                {t("projects.cancel")}
              </Button>
            </form>
          )}

          <TaskListView
            tasks={filteredTasks}
            lists={lists}
            users={users}
            tenantTz={tenant?.timezone}
            projectId={projectId}
            currentUser={user}
            sortBy={sortBy}
            groupBy={groupBy}
            showCompleted={showCompleted}
            showRecurring={showRecurring}
            overdueOnly={overdueOnly}
            activeListId={listFilter}
            openComposerToken={addComposerToken}
            onOverdueChange={(v) => {
              const next = new URLSearchParams(searchParams);
              if (v) next.set("overdue", "1");
              else next.delete("overdue");
              setSearchParams(next, { replace: true });
            }}
            onSortByChange={setSortBy}
            onGroupByChange={setGroupBy}
            onShowCompletedChange={setShowCompleted}
            onShowRecurringChange={setShowRecurring}
            onOpen={openTask}
            onUpdate={safeUpdateTask}
            onDelete={(task) => setConfirmDelete(task)}
            onCreate={async (payload) => {
              try {
                await createTask(payload);
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
            }}
          />
        </TabsContent>

        <TabsContent value="kanban" className="mt-6">
          <KanbanView
            lists={lists}
            tasks={filteredTasks}
            users={users}
            currentUser={user}
            onOpen={(t) => openTask(t)}
            onUpdate={(id, patch) => updateTask(id, patch)}
          />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <CalendarView tasks={filteredTasks} tenantTz={tenant?.timezone} onOpen={openTask} />
        </TabsContent>
      </Tabs>

      {panelTask && (
        <TaskRightPanel
          task={panelTask}
          initialTab={panelInitialTab}
          users={users}
          lists={lists}
          onClose={() => setPanelTask(null)}
          onExpand={expandToDialog}
          onSave={async (patch) => {
            await updateTask(panelTask.id, patch);
          }}
          onDelete={async () => {
            await deleteTask(panelTask.id);
            setPanelTask(null);
            toast.success("Deleted");
          }}
          tenantTz={tenant?.timezone}
        />
      )}

      {dialogTask && (
        <TaskDialog
          task={dialogTask}
          onClose={() => setDialogTask(null)}
          users={users}
          onSave={async (patch) => {
            await updateTask(dialogTask.id, patch);
            toast.success("Updated");
          }}
          onDelete={async () => {
            await deleteTask(dialogTask.id);
            setDialogTask(null);
            toast.success("Deleted");
          }}
          tenantTz={tenant?.timezone}
        />
      )}

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tasks.delete")}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.title} — {t("tasks.delete_confirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">
              {t("common.no")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={async () => {
                if (confirmDelete) {
                  await deleteTask(confirmDelete.id);
                  toast.success("Deleted");
                }
                setConfirmDelete(null);
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={announceOpen} onOpenChange={setAnnounceOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-600" /> Duyuru yayınla
            </DialogTitle>
            <DialogDescription>
              Bu mesaj <b>#{project?.name || "kanal"}</b> kanalına gönderilip
              tenant çapında sabitlenecek. Tüm çalışanlara altın renkli banner
              olarak görünür.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="announce-body" className="text-xs uppercase tracking-wider text-zinc-500">
              Duyuru metni
            </Label>
            <Textarea
              id="announce-body"
              value={announceBody}
              onChange={(e) => setAnnounceBody(e.target.value)}
              placeholder={
                isMeeting
                  ? "Toplantı gündemi (opsiyonel)"
                  : "Örn: Cuma 15:00 tüm ekip toplantısı! Katılım zorunlu."
              }
              rows={isMeeting ? 3 : 4}
              data-testid="announce-body"
              className="text-sm"
            />
          </div>

          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="is-meeting"
                checked={isMeeting}
                onCheckedChange={(v) => setIsMeeting(!!v)}
                data-testid="announce-is-meeting"
              />
              <Label
                htmlFor="is-meeting"
                className="cursor-pointer text-sm font-medium"
              >
                Toplantı duyurusu
              </Label>
            </div>

            {isMeeting && (
              <div
                className="space-y-3 border-l-2 border-amber-400 pl-3"
                data-testid="meeting-fields"
              >
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    Katılım tipi
                  </Label>
                  <RadioGroup
                    value={meetingMode}
                    onValueChange={setMeetingMode}
                    className="flex gap-4"
                  >
                    <label
                      htmlFor="mode-online"
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <RadioGroupItem
                        id="mode-online"
                        value="online"
                        data-testid="meeting-mode-online"
                      />
                      Online
                    </label>
                    <label
                      htmlFor="mode-physical"
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <RadioGroupItem
                        id="mode-physical"
                        value="physical"
                        data-testid="meeting-mode-physical"
                      />
                      Fiziksel
                    </label>
                  </RadioGroup>
                </div>

                {meetingMode === "online" ? (
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="meeting-link"
                      className="text-xs uppercase tracking-wider text-zinc-500"
                    >
                      Online toplantı bağlantısı
                    </Label>
                    <Input
                      id="meeting-link"
                      type="url"
                      value={meetingLink}
                      onChange={(e) => setMeetingLink(e.target.value)}
                      placeholder="https://meet.google.com/... veya https://meet.jit.si/..."
                      data-testid="meeting-link"
                      className="text-sm"
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="meeting-place"
                      className="text-xs uppercase tracking-wider text-zinc-500"
                    >
                      Toplantı yeri
                    </Label>
                    <Input
                      id="meeting-place"
                      value={meetingPlace}
                      onChange={(e) => setMeetingPlace(e.target.value)}
                      placeholder="Örn: 3. Kat A Toplantı Salonu"
                      data-testid="meeting-place"
                      className="text-sm"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="meeting-at"
                      className="text-xs uppercase tracking-wider text-zinc-500"
                    >
                      Tarih ve saat
                    </Label>
                    <Input
                      id="meeting-at"
                      type="datetime-local"
                      step={1800}
                      value={meetingAt}
                      onChange={(e) => setMeetingAt(e.target.value)}
                      data-testid="meeting-at"
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="meeting-duration"
                      className="text-xs uppercase tracking-wider text-zinc-500"
                    >
                      Süre
                    </Label>
                    <select
                      id="meeting-duration"
                      value={meetingDuration}
                      onChange={(e) =>
                        setMeetingDuration(parseInt(e.target.value, 10))
                      }
                      data-testid="meeting-duration"
                      className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value={15}>15 dakika</option>
                      <option value={30}>30 dakika</option>
                      <option value={45}>45 dakika</option>
                      <option value={60}>1 saat</option>
                      <option value={90}>1 saat 30 dk</option>
                      <option value={120}>2 saat</option>
                      <option value={180}>3 saat</option>
                      <option value={240}>4 saat</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="meeting-required"
                    checked={meetingRequired}
                    onCheckedChange={(v) => setMeetingRequired(!!v)}
                    data-testid="meeting-required"
                  />
                  <Label
                    htmlFor="meeting-required"
                    className="cursor-pointer text-sm"
                  >
                    Zorunlu katılım
                    <span className="ml-1 text-[11px] text-zinc-500">
                      (opsiyonel)
                    </span>
                  </Label>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="announce-expires-at"
              className="text-xs uppercase tracking-wider text-zinc-500"
            >
              Son tarih (opsiyonel)
            </Label>
            <Input
              id="announce-expires-at"
              type="datetime-local"
              step={1800}
              value={announceExpiresAt}
              onChange={(e) => setAnnounceExpiresAt(e.target.value)}
              data-testid="announce-expires-at"
              className="text-sm"
            />
            <p className="text-[11px] text-zinc-500">
              {isMeeting
                ? "Boş bırakılırsa toplantı bittiğinde (bitiş saatinde) otomatik silinir."
                : "Boş bırakılırsa duyuru manuel kapatılana dek görünür kalır. Belirtilen tarihte otomatik olarak silinir."}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setAnnounceOpen(false)}
              data-testid="announce-cancel"
            >
              İptal
            </Button>
            <Button
              type="button"
              onClick={publishAnnouncement}
              disabled={
                publishing ||
                (!isMeeting && !announceBody.trim()) ||
                (isMeeting &&
                  (!meetingAt ||
                    (meetingMode === "online" && !meetingLink.trim()) ||
                    (meetingMode === "physical" && !meetingPlace.trim())))
              }
              data-testid="announce-publish"
              className="gap-2 bg-amber-500 text-white hover:bg-amber-600"
            >
              <Megaphone className="h-4 w-4" />
              {publishing ? "Yayınlanıyor..." : "Yayınla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-project-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bu projeyi silmek istediğinden emin misin?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-zinc-600">
                <p>
                  <b>{project?.name || "Bu proje"}</b> ve içindeki{" "}
                  <b>tüm görevler, gruplar (listeler) ve takımlar</b> kalıcı
                  olarak silinecek.
                </p>
                <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                  Bu işlem geri alınamaz.
                </p>
                <div className="pt-2">
                  <Label
                    htmlFor="delete-project-confirm"
                    className="text-xs tracking-wider text-zinc-500"
                  >
                    Onaylamak için proje adını yaz: <b className="text-zinc-900">{project?.name}</b>
                  </Label>
                  <Input
                    id="delete-project-confirm"
                    autoFocus
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder={project?.name || ""}
                    data-testid="delete-project-confirm-input"
                    className="mt-1"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-project-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                deleting ||
                deleteConfirmName.trim().toLocaleLowerCase("tr") !==
                  (project?.name || "").trim().toLocaleLowerCase("tr")
              }
              onClick={(e) => {
                e.preventDefault();
                deleteProject();
              }}
              data-testid="delete-project-confirm-btn"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Siliniyor..." : "Kalıcı olarak sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteListTarget}
        onOpenChange={(o) => !o && !deletingList && setDeleteListTarget(null)}
      >
        <AlertDialogContent data-testid="delete-list-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bu grubu silmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-zinc-600">
                <p>
                  <b>{deleteListTarget?.name || "Bu grup"}</b> ve içindeki
                  <b> tüm görevler</b> kalıcı olarak silinecek.
                </p>
                <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
                  Bu işlem geri alınamaz. Projeyi silmez, sadece bu grubu ve görevlerini kaldırır.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deletingList}
              data-testid="delete-list-cancel"
            >
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingList}
              onClick={(e) => {
                e.preventDefault();
                deleteListHandler();
              }}
              data-testid="delete-list-confirm-btn"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deletingList ? "Siliniyor..." : "Grubu sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename group (list) dialog. */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(o) => {
          if (!o && !savingRename) setRenameTarget(null);
        }}
      >
        <DialogContent data-testid="rename-list-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Grubu yeniden adlandır</DialogTitle>
            <DialogDescription>
              Bu grubun görünen adını değiştir. Görevler etkilenmez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-list-input" className="text-xs uppercase tracking-wider text-zinc-500">
              Grup adı
            </Label>
            <Input
              id="rename-list-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitRename();
                }
              }}
              data-testid="rename-list-input"
              placeholder="Yeni grup adı"
              autoFocus
              maxLength={80}
              disabled={savingRename}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRenameTarget(null)}
              disabled={savingRename}
              data-testid="rename-list-cancel"
            >
              Vazgeç
            </Button>
            <Button
              type="button"
              onClick={submitRename}
              disabled={savingRename || !renameValue.trim()}
              data-testid="rename-list-save"
              className="bg-zinc-900 text-white hover:bg-zinc-800"
            >
              {savingRename ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emoji picker dialog — lazy-loaded to keep the initial bundle lean. */}
      <Dialog
        open={!!emojiTarget}
        onOpenChange={(o) => {
          if (!o) setEmojiTarget(null);
        }}
      >
        <DialogContent
          data-testid="emoji-picker-dialog"
          className="max-w-fit p-3"
        >
          <DialogHeader className="mb-1">
            <DialogTitle className="text-sm">
              {emojiTarget?.name
                ? `"${emojiTarget.name}" için emoji seç`
                : "Emoji seç"}
            </DialogTitle>
          </DialogHeader>
          <Suspense
            fallback={
              <div className="flex h-[400px] w-[350px] items-center justify-center text-xs text-zinc-500">
                Emoji seçici yükleniyor…
              </div>
            }
          >
            <EmojiPicker
              width={350}
              height={420}
              onEmojiClick={(data) => {
                const target = emojiTarget;
                if (!target) return;
                setEmojiTarget(null);
                patchList(
                  target,
                  { emoji: data?.emoji || "" },
                  `Emoji ayarlandı: ${data?.emoji || ""}`,
                );
              }}
              searchPlaceHolder="Emoji ara..."
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              lazyLoadEmojis
            />
          </Suspense>
        </DialogContent>
      </Dialog>
    </div>
  );
}
